import React from 'react';
import { Button, Input, InputNumber, Space, Tag, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { ProductCategory } from '@shared/api/hooks/useProducts';

export interface SizeRow {
  size: string;
  stock: number;
}

const ONE_SIZE = 'TAGLIA UNICA';

/** Preset taglie per categoria. Admin può comunque aggiungere taglie custom. */
const PRESETS: Record<ProductCategory, string[]> = {
  JERSEY: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
  ACCESSORY: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
  SCARF: [ONE_SIZE],
  HAT: [ONE_SIZE],
  OTHER: [ONE_SIZE],
};

interface Props {
  value?: SizeRow[];
  onChange?: (value: SizeRow[]) => void;
  category?: ProductCategory;
}

/**
 * Controlled form field for product sizes + per-size stock.
 * Renders category-driven preset chips (toggle to add/remove) plus
 * free-text custom size rows. Shows total stock and flags duplicates.
 */
export function SizeStockField({ value = [], onChange, category }: Props): React.ReactElement {
  const rows = value;
  const preset = category ? PRESETS[category] : [];

  const emit = (next: SizeRow[]): void => onChange?.(next);

  const norm = (s: string): string => s.trim().toUpperCase();

  const togglePreset = (size: string): void => {
    const exists = rows.some((r) => norm(r.size) === norm(size));
    if (exists) {
      emit(rows.filter((r) => norm(r.size) !== norm(size)));
    } else {
      emit([...rows, { size, stock: 0 }]);
    }
  };

  const updateRow = (idx: number, patch: Partial<SizeRow>): void => {
    emit(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeRow = (idx: number): void => {
    emit(rows.filter((_, i) => i !== idx));
  };

  const addCustom = (): void => {
    emit([...rows, { size: '', stock: 0 }]);
  };

  const totalStock = rows.reduce((sum, r) => sum + (Number(r.stock) || 0), 0);

  // Duplicate detection (after normalization)
  const counts = new Map<string, number>();
  rows.forEach((r) => {
    const k = norm(r.size);
    if (k) counts.set(k, (counts.get(k) ?? 0) + 1);
  });
  const isDup = (size: string): boolean => (counts.get(norm(size)) ?? 0) > 1;

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {preset.length > 0 && (
        <Space wrap size={4}>
          {preset.map((size) => {
            const checked = rows.some((r) => norm(r.size) === norm(size));
            return (
              <Tag.CheckableTag
                key={size}
                checked={checked}
                onChange={() => togglePreset(size)}
                style={{ border: '1px solid #d9d9d9', padding: '2px 10px', userSelect: 'none' }}
              >
                {size}
              </Tag.CheckableTag>
            );
          })}
        </Space>
      )}

      {rows.length === 0 ? (
        <Typography.Text type="secondary">
          Nessuna taglia — seleziona un preset o aggiungi taglia custom.
        </Typography.Text>
      ) : (
        rows.map((row, idx) => (
          <Space key={idx} align="baseline">
            <Input
              value={row.size}
              placeholder="Taglia"
              status={isDup(row.size) ? 'error' : undefined}
              onChange={(e) => updateRow(idx, { size: e.target.value })}
              style={{ width: 150 }}
            />
            <InputNumber
              value={row.stock}
              min={0}
              placeholder="0"
              addonAfter="pz"
              onChange={(v) => updateRow(idx, { stock: Number(v) || 0 })}
              style={{ width: 120 }}
            />
            <Button danger size="small" icon={<DeleteOutlined />} onClick={() => removeRow(idx)} />
            {isDup(row.size) && (
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                duplicata
              </Typography.Text>
            )}
          </Space>
        ))
      )}

      <Space>
        <Button onClick={addCustom} icon={<PlusOutlined />} size="small">
          Taglia custom
        </Button>
        {rows.length > 0 && (
          <Typography.Text type="secondary">
            Totale scorte: <strong>{totalStock}</strong> pz · {rows.length} taglie
          </Typography.Text>
        )}
      </Space>
    </Space>
  );
}
